import { Button, Flex, Loader, Stack } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { useGetDocument } from 'entities/document/api';
import { useDuplicateDocument } from 'features/document-duplicate/hooks/use-duplicate-document';
import { useTranslation } from 'react-i18next';
import { CheckboxField, TextField } from 'shared/ui/Fields';
import { Form, FormButton } from 'shared/ui/Form';

type DuplicateDocumentModalBodyProps = {
  documentId: number;
  onClose: () => void;
  onDuplicated: (newDocument: { id: number | string; name: string }) => void;
};

const DuplicateDocumentModalBody: React.FC<DuplicateDocumentModalBodyProps> = ({
  documentId,
  onClose,
  onDuplicated,
}) => {
  const { t } = useTranslation(['common', 'documents']);
  const { data: document, isInitialLoading } = useGetDocument(documentId);
  const { duplicate } = useDuplicateDocument(documentId);

  const handleSubmit = async (data: {
    name: string;
    copy: { queue: boolean; attachments: boolean; branding: boolean };
  }) => {
    const result = await duplicate(data);
    if (result) {
      onDuplicated(result);
    } else {
      onClose();
    }
  };

  if (isInitialLoading) {
    return (
      <Flex h={286} justify="center" align="center">
        <Loader />
      </Flex>
    );
  }

  const defaultValues = {
    name: document?.name,
    copy: {
      queue: false,
      attachments: false,
      branding: false,
    },
  };

  return (
    <Form onSubmit={handleSubmit} defaultValues={defaultValues}>
      <TextField
        name="name"
        label={t('documents:duplicateModal.name')}
        data-testid="field-duplicate-document-name"
      />
      <Stack spacing={16}>
        <CheckboxField
          name="copy.queue"
          label={t('documents:duplicateModal.copy.queue')}
          data-testid="field-duplicate-document-queue"
        />
        <CheckboxField
          name="copy.attachments"
          label={t('documents:duplicateModal.copy.attachments')}
          data-testid="field-duplicate-document-attachments"
        />
        <CheckboxField
          name="copy.branding"
          label={t('documents:duplicateModal.copy.branding')}
          data-testid="field-duplicate-document-branding"
        />
      </Stack>
      <Flex direction="row-reverse" gap={16}>
        <FormButton type="submit" data-testid="btn-confirm-duplicate-document">
          {t('common:duplicate')}
        </FormButton>
        <Button
          onClick={onClose}
          variant="default"
          data-testid="btn-cancel-duplicate-document"
        >
          {t('common:cancel')}
        </Button>
      </Flex>
    </Form>
  );
};

const MODAL_ID = 'duplicate-document-modal';

const Title = () => {
  const { t } = useTranslation(['documents']);
  return <>{t('documents:duplicateModal.title')}</>;
};

export type DuplicateDocumentModalProps = {
  documentId: number;
  onDuplicated?: (newDocument: { id: number | string; name: string }) => void;
};

export const duplicateDocumentModal = ({
  documentId,
  onDuplicated,
}: DuplicateDocumentModalProps) => {
  openModal({
    modalId: MODAL_ID,
    title: <Title />,
    size: 'lg',
    centered: true,
    children: (
      <DuplicateDocumentModalBody
        documentId={documentId}
        onClose={() => closeModal(MODAL_ID)}
        onDuplicated={result => {
          closeModal(MODAL_ID);
          onDuplicated?.(result);
        }}
      />
    ),
  });
};
